import { useEffect, useMemo, useRef, useState } from 'react';
import { GripVertical, Package, Plus, Star, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ServicesPickerProps {
  value: string[];
  onChange: (next: string[]) => void;
  presets?: Record<string, string[]>;
  primaryCount?: number;
  placeholder?: string;
  /** Brand Bible v2 fold-in (design spec item 4): price-policy dot per chip.
   * Returns null when the chip has no v2 record yet — no dot renders. */
  chipDot?: (label: string) => 'publish' | 'quote_only' | 'unset' | null;
  /** Selected chip name — renders a ring and is the source for the detail row below. */
  selectedChip?: string | null;
  onSelectChip?: (label: string | null) => void;
  /** D57: asked before a chip is removed; resolve false to keep it. Absent = remove at once. */
  confirmRemove?: (label: string) => Promise<boolean>;
}

const DOT_CLASS: Record<'publish' | 'quote_only' | 'unset', string> = {
  publish: 'bg-[hsl(var(--status-success))]',
  quote_only: 'bg-[hsl(var(--status-warning))]',
  unset: 'bg-border',
};

const DEFAULT_PRESETS: Record<string, string[]> = {
  Plumbing: [
    'Emergency repairs',
    'Water heater install',
    'Drain cleaning',
    'Repipe',
    'Sump pumps',
    'Leak repair',
    'Toilet install',
    'Garbage disposal',
    'Hydro jetting',
    'Gas lines',
    'Sewer line',
    'Backflow testing',
  ],
  HVAC: [
    'AC repair',
    'Furnace install',
    'Heat pump',
    'Ductwork',
    'Tune-ups',
    'Indoor air quality',
    'Thermostat install',
  ],
  Electrical: [
    'Panel upgrade',
    'EV charger install',
    'Generator install',
    'Recessed lighting',
    'Outlet & switch',
    'Whole-home rewire',
  ],
  Roofing: ['Roof replacement', 'Roof repair', 'Gutters', 'Inspections', 'Storm damage'],
  Landscaping: ['Lawn care', 'Tree service', 'Hardscaping', 'Irrigation', 'Snow removal'],
};

type FlatOption = { kind: 'item'; label: string } | { kind: 'create'; label: string };

export function ServicesPicker({
  value = [],
  onChange,
  presets = DEFAULT_PRESETS,
  primaryCount = 3,
  placeholder = 'Search services or type to add…',
  chipDot,
  selectedChip = null,
  onSelectChip,
  confirmRemove,
}: ServicesPickerProps) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [showPresets, setShowPresets] = useState(false);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  const wrapRef = useRef<HTMLDivElement>(null);

  const allKnown = useMemo(() => {
    const set = new Set<string>();
    Object.values(presets).forEach((arr) => arr.forEach((s) => set.add(s)));
    return Array.from(set);
  }, [presets]);

  const flatOptions = useMemo<FlatOption[]>(() => {
    const out: FlatOption[] = [];
    const filtered = allKnown
      .filter((s) => !value.includes(s) && s.toLowerCase().includes(q.toLowerCase()));
    filtered.slice(0, 8).forEach((s) => out.push({ kind: 'item', label: s }));
    if (
      q.trim() &&
      !allKnown.some((s) => s.toLowerCase() === q.trim().toLowerCase()) &&
      !value.includes(q.trim())
    ) {
      out.push({ kind: 'create', label: q.trim() });
    }
    return out;
  }, [q, value, allKnown]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const add = (label: string) => {
    if (!label || value.includes(label)) return;
    onChange([...value, label]);
    setQ('');
    setActiveIdx(0);
  };
  const remove = (i: number) => {
    const label = value[i];
    if (!confirmRemove || label === undefined) {
      const n = [...value];
      n.splice(i, 1);
      onChange(n);
      return;
    }
    void confirmRemove(label).then((yes) => {
      if (!yes) return;
      const latest = valueRef.current;
      const at = latest.indexOf(label);
      if (at < 0) return;
      const n = [...latest];
      n.splice(at, 1);
      onChange(n);
    });
  };
  const move = (from: number, to: number) => {
    if (from === to || from == null || to == null) return;
    const n = [...value];
    const [it] = n.splice(from, 1);
    n.splice(to, 0, it);
    onChange(n);
  };

  const loadPreset = (key: string) => {
    const next = [...value];
    presets[key].forEach((s) => {
      if (!next.includes(s)) next.push(s);
    });
    onChange(next);
    setShowPresets(false);
  };

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActiveIdx((i) => Math.min(flatOptions.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = flatOptions[activeIdx];
      if (opt) add(opt.label);
      else if (q.trim()) add(q.trim());
    } else if (e.key === 'Backspace' && !q && value.length) {
      remove(value.length - 1);
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <div className="flex gap-1.5 mb-2 flex-wrap">
        <button
          type="button"
          onClick={() => setShowPresets((v) => !v)}
          className={cn(
            'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[12px] font-medium border cursor-pointer',
            showPresets
              ? 'bg-coral-light text-coral-dark border-coral/40'
              : 'bg-card text-muted-foreground border-border hover:text-foreground',
          )}
        >
          <Package className="size-3.5" />
          Load industry preset
        </button>
        <div className="flex-1" />
        <span className="text-[11px] text-muted-foreground self-center">
          Drag to reorder · first {primaryCount} are <strong className="text-coral">primary</strong>
        </span>
      </div>

      {showPresets && (
        <div className="mb-2.5 p-2.5 rounded-lg bg-warm-bg border border-border flex flex-wrap gap-1.5">
          {Object.keys(presets).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => loadPreset(k)}
              className="px-2.5 py-1 rounded-md text-[12px] font-medium bg-card border border-border text-foreground hover:border-coral cursor-pointer inline-flex items-center gap-1.5"
            >
              {k}
              <span className="text-[10.5px] text-muted-foreground">+{presets[k].length}</span>
            </button>
          ))}
        </div>
      )}

      <div
        onClick={(e) => {
          if (e.target === e.currentTarget) inputRef.current?.focus();
        }}
        className={cn(
          'flex flex-wrap gap-1.5 items-center px-2 py-1.5 min-h-[42px] rounded-lg bg-card cursor-text',
          'transition-[border-color,box-shadow] duration-150 border',
          focused ? 'border-coral ring-[3px] ring-coral/15' : 'border-border',
        )}
      >
        {value.map((v, i) => {
          const isPrimary = i < primaryCount;
          const isOver = overIdx === i && dragIdx !== null && dragIdx !== i;
          const dot = chipDot?.(v) ?? null;
          const isSelected = onSelectChip && selectedChip === v;
          return (
            <span
              key={v + i}
              draggable
              onDragStart={(e) => {
                setDragIdx(i);
                e.dataTransfer.effectAllowed = 'move';
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setOverIdx(i);
              }}
              onDragEnd={() => {
                if (dragIdx !== null && overIdx !== null) move(dragIdx, overIdx);
                setDragIdx(null);
                setOverIdx(null);
              }}
              onDrop={(e) => e.preventDefault()}
              onClick={() => onSelectChip?.(selectedChip === v ? null : v)}
              className={cn(
                'inline-flex items-center gap-1 px-1 py-0.5 rounded-md text-[12.5px] font-medium border transition-[outline] duration-100',
                isPrimary
                  ? 'bg-coral-light text-coral-dark border-coral/40'
                  : 'bg-warm-bg text-foreground border-border',
                dragIdx === i ? 'opacity-40 cursor-grabbing' : 'cursor-grab',
                isOver && 'outline outline-2 outline-coral outline-offset-2',
                isSelected && 'ring-2 ring-coral ring-offset-1',
              )}
            >
              <GripVertical
                className={cn(
                  'size-3.5 cursor-grab',
                  isPrimary ? 'text-coral' : 'text-muted-foreground',
                )}
              />
              {isPrimary && <Star className="size-3 text-coral fill-coral" />}
              {dot && (
                <span
                  title={`price policy: ${dot === 'unset' ? 'not set' : dot.replace('_', ' ')}`}
                  className={cn('size-1.5 rounded-full flex-shrink-0', DOT_CLASS[dot])}
                />
              )}
              <span className="px-1">{v}</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  remove(i);
                }}
                className={cn(
                  'grid place-items-center w-4 h-4 rounded bg-transparent border-none cursor-pointer',
                  isPrimary ? 'text-coral-dark' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <X className="size-3" />
              </button>
            </span>
          );
        })}
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
            setActiveIdx(0);
          }}
          onKeyDown={onKey}
          onFocus={() => {
            setFocused(true);
            setOpen(true);
          }}
          onBlur={() => setFocused(false)}
          placeholder={value.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[140px] border-none outline-none px-1 py-1 text-[13.5px] bg-transparent"
        />
      </div>

      {open && flatOptions.length > 0 && (
        <div className="absolute top-[calc(100%+4px)] left-0 right-0 z-30 bg-card border border-border rounded-xl shadow-2xl max-h-[240px] overflow-auto p-1">
          {flatOptions.map((o, i) => {
            const isActive = i === activeIdx;
            return (
              <button
                key={o.kind + o.label + i}
                type="button"
                onMouseEnter={() => setActiveIdx(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  add(o.label);
                }}
                className={cn(
                  'flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md border-none text-left cursor-pointer text-[13px] text-foreground',
                  isActive ? 'bg-coral-light' : 'bg-transparent',
                )}
              >
                {o.kind === 'create' ? (
                  <>
                    <Plus className="size-3 text-coral" />
                    <span>
                      Add <strong className="font-semibold">"{o.label}"</strong>
                    </span>
                    <span className="ml-auto text-[10.5px] text-muted-foreground">custom</span>
                  </>
                ) : (
                  <>
                    <Plus className="size-3 text-muted-foreground" />
                    <span>{o.label}</span>
                  </>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
